import * as Yup from "yup";
import "../accountsInfo.css";
import { useState, useRef } from "react";
import { Eye, EyeOff } from "lucide-react";
import Toaster from "../../../components/Toast/Toast";
import { useDispatch, useSelector } from "react-redux";
import { toggleReFetch } from "../../../redux/slices/themeSlice";
import { updatePassword } from "../../../api/modules/b2droppie.class";
import BackdropLoader from "../../../components/BackdropLoader/BackdropLoader";

const UpdatePassword = () => {
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ success: "", error: "" });
  const isDarkMode = useSelector((state) => state.theme.darkMode);

  const initialFormDataRef = useRef({
    newPassword: "",
    currentPassword: "",
    confirmPassword: "",
  });

  const [formData, setFormData] = useState({
    newPassword: "",
    currentPassword: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState({
    newPassword: false,
    currentPassword: false,
    confirmPassword: false,
  });

  const togglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const validationSchema = Yup.object().shape({
    currentPassword: Yup.string().required("Current password is required"),
    newPassword: Yup.string()
      .required("New password is required")
      .min(8, "Password must be at least 8 characters")
      .matches(/[A-Z]/, "Must contain at least one uppercase letter")
      .matches(/[a-z]/, "Must contain at least one lowercase letter")
      .matches(/[0-9]/, "Must contain at least one number")
      .matches(
        /[@$!%*?&#^()_+\-=[\]{};':"\\|,.<>/?]/,
        "Must contain at least one special character",
      )
      .notOneOf(
        [Yup.ref("currentPassword")],
        "New password cannot be the same as current password",
      ),
    confirmPassword: Yup.string()
      .required("Confirm new password is required")
      .oneOf([Yup.ref("newPassword"), null], "Passwords must match"),
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) {
      validationSchema
        .validateAt(name, { ...formData, [name]: value })
        .then(() => setErrors((prev) => ({ ...prev, [name]: "" })))
        .catch((err) =>
          setErrors((prev) => ({ ...prev, [name]: err.message })),
        );
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    validationSchema
      .validateAt(name, formData)
      .then(() => setErrors((prev) => ({ ...prev, [name]: "" })))
      .catch((err) => setErrors((prev) => ({ ...prev, [name]: err.message })));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!isFormChanged) {
      setToast({ success: "", error: "There are no changes to save." });
      setLoading(false);
      return;
    }

    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setErrors({});

      const payload = {
        new_password: formData.newPassword,
        current_password: formData.currentPassword,
        new_password_confirmation: formData.confirmPassword,
      };

      const response = await updatePassword(payload);
      const message = response?.data?.message;

      if (response?.data?.status === 200 || response?.data?.success) {
        dispatch(toggleReFetch("mine"));
        setToast({
          error: "",
          success: message || "Password updated successfully.",
        });
        setFormData({
          newPassword: "",
          currentPassword: "",
          confirmPassword: "",
        });
        initialFormDataRef.current = {
          newPassword: "",
          currentPassword: "",
          confirmPassword: "",
        };
      } else {
        setToast({
          success: "",
          error: message || "Current password is incorrect.",
        });
      }
    } catch (err) {
      if (err.inner) {
        const formattedErrors = {};
        const allTouched = {};

        err.inner.forEach((e) => {
          formattedErrors[e.path] = e.message;
          allTouched[e.path] = true;
        });

        setErrors(formattedErrors);
        setTouched((prev) => ({ ...prev, ...allTouched }));
      } else {
        setToast({ success: "", error: "Something went wrong. Try again." });
      }
    } finally {
      setLoading(false);
    }
  };

  const isFormChanged = Object.keys(initialFormDataRef.current).some(
    (key) => formData[key] !== initialFormDataRef.current[key],
  );

  return (
    <>
      <Toaster message={toast} />
      <div className={`user-info-outer ${isDarkMode ? "dark" : "light"}`}>
        <h2 className="title mb-0">Change Password</h2>

        <form onSubmit={handleSubmit} className="mt-32px">
          <div className="container-fluid">
            <div className="row">
              {/* Current Password */}
              <div className="col-12 px-0">
                <label className="form-label">Old Password</label>
                <div className="password-input-wrapper">
                  <input
                    onBlur={handleBlur}
                    name="currentPassword"
                    onChange={handleChange}
                    placeholder="*************"
                    value={formData.currentPassword}
                    type={showPassword.currentPassword ? "text" : "password"}
                    className={`form-psi-input ${
                      errors.currentPassword && touched.currentPassword
                        ? "error"
                        : ""
                    }`}
                  />
                  <span
                    className="password-toggle-icon"
                    onClick={() => togglePasswordVisibility("currentPassword")}
                  >
                    {showPassword.currentPassword ? (
                      <EyeOff size={18} />
                    ) : (
                      <Eye size={18} />
                    )}
                  </span>
                </div>
                {errors.currentPassword && touched.currentPassword && (
                  <span className="error-message">
                    {errors.currentPassword}
                  </span>
                )}
              </div>

              {/* New Password */}
              <div className="col-md-6 col-12 mt-4 ps-md-0 pe-md-2 px-0">
                <label className="form-label">New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword.newPassword ? "text" : "password"}
                    name="newPassword"
                    placeholder="*************"
                    value={formData.newPassword}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    className={`form-psi-input ${
                      errors.newPassword && touched.newPassword ? "error" : ""
                    }`}
                  />
                  <span
                    className="password-toggle-icon"
                    onClick={() => togglePasswordVisibility("newPassword")}
                  >
                    {showPassword.newPassword ? (
                      <EyeOff size={18} />
                    ) : (
                      <Eye size={18} />
                    )}
                  </span>
                </div>
                {errors.newPassword && touched.newPassword && (
                  <span className="error-message">{errors.newPassword}</span>
                )}
              </div>

              {/* Confirm New Password */}
              <div className="col-md-6 col-12 mt-4 ps-md-2 pe-md-0 px-0">
                <label className="form-label">Confirm New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword.confirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    placeholder="*************"
                    value={formData.confirmPassword}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    className={`form-psi-input ${
                      errors.confirmPassword && touched.confirmPassword
                        ? "error"
                        : ""
                    }`}
                  />
                  <span
                    className="password-toggle-icon"
                    onClick={() => togglePasswordVisibility("confirmPassword")}
                  >
                    {showPassword.confirmPassword ? (
                      <EyeOff size={18} />
                    ) : (
                      <Eye size={18} />
                    )}
                  </span>
                </div>
                {errors.confirmPassword && touched.confirmPassword && (
                  <span className="error-message">
                    {errors.confirmPassword}
                  </span>
                )}
              </div>

              <div className="d-end gap-10px my-auto mt-4 p-0">
                <button
                  type="submit"
                  className="filled-medium-deep-yellow-btn-dark w-lg-20 w-md-50"
                >
                  Update <span className="dsp-pass">Password</span>
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
      <BackdropLoader show={loading} />
    </>
  );
};

export default UpdatePassword;
